import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { Mentor } from '../../../mocks/mentors';
import { api } from '@/lib/api';
import { adaptLecturer } from '@/lib/adapters';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const FeaturedMentors = () => {
  const navigate = useNavigate();
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation<HTMLDivElement>({ threshold: 0.2 });
  const { ref: gridRef, isVisible: gridVisible } = useScrollAnimation<HTMLDivElement>({ threshold: 0.08 });
  const [mentors, setMentors] = useState<Mentor[]>([]);

  useEffect(() => {
    void api.searchLecturers({ pageCurrent: 1, pageSize: 3 }).then((page) => {
      setMentors(page.list.map(adaptLecturer));
    });
  }, []);

  return (
    <section className="py-16 sm:py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div
          ref={headerRef}
          className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10 sm:mb-14"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.7s cubic-bezier(0.22,1,0.36,1), transform 0.7s cubic-bezier(0.22,1,0.36,1)'
          }}
        >
          <div>
            <span className="text-xs font-semibold text-violet-600 tracking-widest uppercase mb-3 block">
              精選導師陣容
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold text-navy-800 tracking-tight">
              來自後端真實講師資料
            </h2>
          </div>
          <Link
            to="/mentors"
            className="text-sm font-semibold text-violet-600 hover:text-violet-700 flex items-center gap-1 cursor-pointer whitespace-nowrap transition-colors"
          >
            瀏覽全部導師
            <i className="ri-arrow-right-line" />
          </Link>
        </div>

        <div ref={gridRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {mentors.map((mentor, index) => (
            <div
              key={mentor.id}
              onClick={() => navigate(`/mentor/${mentor.id}`)}
              className="bg-white rounded-2xl overflow-hidden border border-slate-100 hover:border-violet-200 transition-all group cursor-pointer"
              style={{
                opacity: gridVisible ? 1 : 0,
                transform: gridVisible ? 'translateY(0)' : 'translateY(40px)',
                transition: 'opacity 0.65s cubic-bezier(0.22,1,0.36,1), transform 0.65s cubic-bezier(0.22,1,0.36,1)',
                transitionDelay: `${index * 130}ms`
              }}
            >
              <div className="relative h-56 overflow-hidden">
                <img src={mentor.avatar} alt={mentor.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                <div
                  className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-xs font-bold"
                  style={{ backgroundColor: mentor.companyColor + '22', color: mentor.companyColor }}
                >
                  {mentor.company}
                </div>
              </div>

              <div className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-bold text-navy-800 text-base">{mentor.name}</h3>
                    <p className="text-slate-500 text-sm mt-0.5">{mentor.title}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="flex items-center gap-1 justify-end">
                      <i className="ri-star-fill text-amber-400 text-xs" />
                      <span className="text-sm font-bold text-navy-800">{mentor.rating}</span>
                    </div>
                    <div className="text-slate-400 text-xs">{mentor.reviews} 評價</div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-4">
                  {mentor.skills.slice(0, 3).map((skill) => (
                    <span key={skill} className="px-2.5 py-1 bg-violet-50 text-violet-700 text-xs font-medium rounded-full">
                      {skill}
                    </span>
                  ))}
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                  <div>
                    <span className="text-xs text-slate-400">起價</span>
                    <div className="text-navy-800 font-bold text-lg">
                      ${mentor.price}
                      <span className="text-slate-400 text-xs font-normal">/次</span>
                    </div>
                  </div>
                  <Link
                    to={`/mentor/${mentor.id}`}
                    className="px-4 py-2 bg-violet-600 text-white text-xs font-semibold rounded-lg cursor-pointer whitespace-nowrap hover:bg-violet-700 transition-colors"
                  >
                    預約諮詢
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturedMentors;
